import { Close } from '@mui/icons-material';
import {
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Pagination,
    Select,
    Stack,
    Tab,
    Tabs,
    ThemeProvider,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import FileHistoryList from '../components/fileHistoryList';

import { useClientHistory } from 'hooks/useClientHistory';
import { useFileHistory } from 'hooks/useFileHistory';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import appThemeV2 from 'theme/themeV2';
import { getErrorMessage, getErrorStatus } from 'utils/v2/errorHelper';

interface FileHistoryModalProps {
    open: boolean;
    onClose: () => void;
    caseId: string | null;
    clientId: number | null;
}

const FileHistoryDialog = ({ open, onClose, caseId, clientId }: FileHistoryModalProps) => {
    const dispatch = useDispatch();
    const { translate } = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [selectedTab, setSelectedTab] = useState<'case' | 'client'>('case');
    const [clientHistoryPage, setClientHistoryPage] = useState(1);
    const [clientHistoryPerPage, setClientHistoryPerPage] = useState(50);

    const {
        data: events = [],
        isLoading,
        isError,
        error,
    } = useFileHistory(caseId, { enabled: open && Boolean(caseId) });

    const {
        data: clientHistory,
        isLoading: isClientHistoryLoading,
        isError: isClientHistoryError,
        error: clientHistoryError,
    } = useClientHistory(
        clientId,
        { page: clientHistoryPage, perPage: clientHistoryPerPage },
        { enabled: open && selectedTab === 'client' }
    );
    const clientHistoryPages = clientHistory?.meta.pages ?? 0;

    useEffect(() => {
        if (!isError) return;

        dispatch(
            setAlert({
                message: getErrorMessage(error, translate('fileHistoryLoadError')),
                type: 'error',
            })
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError, error, dispatch]);

    useEffect(() => {
        setClientHistoryPage(1);
    }, [clientId]);

    useEffect(() => {
        if (!isClientHistoryError) return;

        const status = getErrorStatus(clientHistoryError);
        const message =
            status === 403
                ? translate('clientHistoryAccessDenied')
                : status === 404
                  ? translate('clientHistoryCandidateNotFound')
                  : getErrorMessage(clientHistoryError, translate('clientHistoryLoadError'));

        dispatch(setAlert({ message, type: 'error' }));
    }, [isClientHistoryError, clientHistoryError, dispatch, translate]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            fullScreen={isMobile}
            scroll="paper"
            aria-labelledby="file-history-dialog-title"
        >
            <DialogTitle
                id="file-history-dialog-title"
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1,
                    py: 1.5,
                    px: { xs: 2, sm: 2.5 },
                }}
            >
                <Stack spacing={0.25} sx={{ minWidth: 0, pr: 0.5 }}>
                    <Typography
                        variant="h6"
                        component="span"
                        sx={{ fontWeight: 700, lineHeight: 1.3 }}
                    >
                        {translate('fileHistory')}
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontWeight: 400, fontSize: 13, lineHeight: 1.35 }}
                    >
                        {translate('fileHistoryModalSubtitle')}
                    </Typography>
                </Stack>
                <IconButton
                    onClick={onClose}
                    edge="end"
                    size="small"
                    aria-label={translate('close')}
                    sx={{ mt: 0.25, flexShrink: 0 }}
                >
                    <Close fontSize="small" />
                </IconButton>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    flex: '1 1 auto',
                    minHeight: 0,
                    overflowY: 'auto',
                    px: { xs: 1.5, sm: 2 },
                    py: 1.5,
                    bgcolor: 'rgba(248, 250, 252, 0.55)',
                }}
            >
                <Stack spacing={1.5}>
                    <Tabs
                        value={selectedTab}
                        onChange={(_, value: 'case' | 'client') => setSelectedTab(value)}
                        variant="fullWidth"
                        aria-label={translate('historyTabs')}
                    >
                        <Tab
                            id="case-history-tab"
                            value="case"
                            label={translate('caseHistory')}
                            aria-controls="case-history-panel"
                        />
                        <Tab
                            id="client-history-tab"
                            value="client"
                            label={translate('clientHistory')}
                            aria-controls="client-history-panel"
                        />
                    </Tabs>
                    <div
                        role="tabpanel"
                        id={`${selectedTab}-history-panel`}
                        aria-labelledby={`${selectedTab}-history-tab`}
                    >
                        {selectedTab === 'case' ? (
                            <FileHistoryList events={events} loading={isLoading} />
                        ) : (
                            <Stack spacing={1.5}>
                                {!isClientHistoryError ? (
                                    <FileHistoryList
                                        events={clientHistory?.data ?? []}
                                        loading={isClientHistoryLoading}
                                        clientId={clientId}
                                        emptyTitle="noClientHistory"
                                        emptyDescription="noClientHistoryDescription"
                                    />
                                ) : null}
                                {!isClientHistoryError &&
                                !isClientHistoryLoading &&
                                clientHistoryPages > 1 ? (
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={1.5}
                                        alignItems="center"
                                        justifyContent="space-between"
                                    >
                                        <FormControl size="small" sx={{ minWidth: 120 }}>
                                            <InputLabel id="client-history-page-size-label">
                                                {translate('historyPageSize')}
                                            </InputLabel>
                                            <Select
                                                labelId="client-history-page-size-label"
                                                value={clientHistoryPerPage}
                                                onChange={(event) => {
                                                    setClientHistoryPage(1);
                                                    setClientHistoryPerPage(
                                                        Number(event.target.value)
                                                    );
                                                }}
                                                label={translate('historyPageSize')}
                                                inputProps={{
                                                    'aria-label': translate('historyPageSize'),
                                                }}
                                            >
                                                <MenuItem value={50}>50</MenuItem>
                                                <MenuItem value={100}>100</MenuItem>
                                                <MenuItem value={200}>200</MenuItem>
                                            </Select>
                                        </FormControl>
                                        <Pagination
                                            count={clientHistoryPages}
                                            page={clientHistoryPage}
                                            onChange={(_, page) => setClientHistoryPage(page)}
                                            size="small"
                                            aria-label={translate('historyPagination')}
                                        />
                                    </Stack>
                                ) : null}
                            </Stack>
                        )}
                    </div>
                </Stack>
            </DialogContent>
        </Dialog>
    );
};

const FileHistoryModal = ({ open, onClose, caseId, clientId }: FileHistoryModalProps) => {
    if (!open) return null;

    return (
        <ThemeProvider theme={appThemeV2}>
            <FileHistoryDialog open={open} onClose={onClose} caseId={caseId} clientId={clientId} />
        </ThemeProvider>
    );
};

export default FileHistoryModal;
